import React from 'react';
import './PaperAssignmentCalendar.css';
import { formatScore, showStudentURL } from '../../../utils/textUtils';
import secret from '../../../secret.json';

const formatGrade = (grade) => {
  const gradeNum = parseInt(grade, 10);
  if (Number.isNaN(gradeNum)) {
    return `${grade}학년`;
  }

  if (gradeNum >= 1 && gradeNum <= 6) {
    return `초등 ${gradeNum}학년`;
  }

  if (gradeNum >= 7 && gradeNum <= 9) {
    return `중등 ${gradeNum - 6}학년`;
  }

  if (gradeNum >= 10 && gradeNum <= 12) {
    return `고등 ${gradeNum - 9}학년`;
  }

  return `${gradeNum}학년`;
};

const formatDateTime = (value) =>
  new Date(value).toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const attitudeVariant = (attitude) => {
  if (!attitude) return "neutral";
  if (attitude === "상") return "positive";
  if (attitude === "중") return "warning";
  if (attitude === "하") return "negative";
  return "neutral";
};

const getContentLabel = (type) => {
  if (type === "lesson") return "수업";
  if (type === "exam") return "시험";
  if (type === "homework") return "과제";
  return "기록";
};

const ClassContentModal = ({ isOpen, record, onClose }) => {
  if (!isOpen || !record) return null;

  const handleFileDownload = (fileUrl, fileName) => {
    if (!fileUrl) return;

    // 새 탭에서 파일 열기 (다운로드)
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // JSON 파싱
  let classContent = record.class_content;
  if (typeof classContent === 'string') {
    try {
      classContent = JSON.parse(classContent);
    } catch (error) {
      classContent = [];
    }
  }

  const hasContent = classContent && Array.isArray(classContent) && classContent.length > 0;
  const hasComment = record.comment && record.comment.trim() !== '';

  return (
    <div
      className="paper-assignment-calendar__modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="paper-assignment-calendar__modal"
        role="dialog"
        aria-modal="true"
        aria-label="수업 정보 상세"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="paper-assignment-calendar__modal-close"
          onClick={onClose}
        >
          ✕
        </button>

        <header className="paper-assignment-calendar__modal-header">
          <div>
            <h3 className="paper-assignment-calendar__modal-title">
              {record.student?.name ?? "이름 미상"}
            </h3>
            <p className="paper-assignment-calendar__modal-subtitle">
              {record.level?.name ?? "반 정보 없음"} · {record.student?.grade ? formatGrade(record.student.grade) : "학년 정보 없음"}
            </p>
            <p className="paper-assignment-calendar__modal-timestamp">
              {formatDateTime(record.created_at)}
            </p>
          </div>
          <div className="paper-assignment-calendar__modal-chips">
            <span
              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                !record.attendance && record.attendance !== false ? "neutral" : record.attendance ? "positive" : "negative"
              }`}
            >
              {!record.attendance && record.attendance !== false ? "출결 해당없음" : record.attendance ? "출석" : "결석"}
            </span>
            <span
              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${attitudeVariant(
                record.attitude
              )}`}
            >
              태도 {record.attitude || "해당없음"}
            </span>
          </div>
        </header>

        {record.report_link && (
          <section className="paper-assignment-calendar__modal-section">
            <h4 className="paper-assignment-calendar__section-title">알림장</h4>
            <a
              href={`${secret.REPORT_BASE_URL}/paper_report/${record.report_link.token}/`.replace(/([^:]\/)\/+/g, '$1')}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#0E141B',
                color: '#FFF',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#00A396'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0E141B'}
            >
              📄 알림장 보기
            </a>
          </section>
        )}

        {record.student && (
          <section className="paper-assignment-calendar__modal-section">
            <h4 className="paper-assignment-calendar__section-title">학생 학습 페이지</h4>
            <a
              href={showStudentURL(record.student.session)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#0E141B',
                color: '#FFF',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#00A396'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#0E141B'}
            >
              🎓 학생 학습 페이지 보기
            </a>
          </section>
        )}

        {hasComment && (
          <section className="paper-assignment-calendar__modal-section">
            <h4 className="paper-assignment-calendar__section-title">코멘트</h4>
            <p className="paper-assignment-calendar__section-content">
              {record.comment}
            </p>
          </section>
        )}

        {hasContent && (
          <section className="paper-assignment-calendar__modal-section">
            <h4 className="paper-assignment-calendar__section-title">수업 내용</h4>
            <ul className="paper-assignment-calendar__content-list">
              {classContent.map((content, index) => {
                const hasFile = content.file_url || content.file_name || content.s3_key;
                const hasScore = content.score !== null && content.score !== undefined && (content.type === 'homework' || content.type === 'exam');
                const formattedScore = formatScore(content.score);
                const formattedTotalScore = formatScore(content.totalScore ?? 100) || '100';

                return (
                  <li key={`${content.type}-${index}`}>
                    <span className="paper-assignment-calendar__content-badge">
                      {getContentLabel(content.type)}
                    </span>
                    <span>{content.description || content.title}</span>
                    {hasScore && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {formattedScore}/{formattedTotalScore}점
                      </span>
                    )}
                    {hasFile && (
                      <a
                        href={content.s3_key ? `${secret.R2_PUBLIC_BASE_URL}/${content.s3_key}` : content.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="paper-assignment-calendar__content-file-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        📎 {content.file_name || '파일 보기'}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {!hasContent && !hasComment && (
          <div className="paper-assignment-calendar__empty" style={{ minHeight: "200px" }}>
            등록된 수업 내용이나 특이사항이 없습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassContentModal;
